import i18n from "i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store/app-state";
import {
  fetchCandidate,
  fetchCandidateDetailOfRanking,
  notifyOpenCandidateDetail,
} from "../../store/candidate/candidate-actions";
import { LoadingIcon } from "../content/loading/loading.styled";
import JobOfferModal from "../job-offer";
import About from "./components/about/about";
import Actions from "./components/actions/actions";
import CandidateSkills from "./components/candidate-skills/candidate-skills";
import Header from "./components/header/header";
import JobOffer from "./components/job-offer";
import PcDs from "./components/pcd/pcd";
import Spotlight from "./components/spotlight/spotlight";
import TimeAvailability from "./components/time-availability";
import useCandidateActions from "./hooks/use-candidate-actions";

import usePages from "../../hooks/use-pages";
import { LastActionStatus } from "../../models/last-action-status.enum";
import { clearCurrentEventTrackData, fetchRecruiterComments } from "../../store/event/event-actions";
import ModalMatch from "../modal-match";
import { Skill } from "./components/candidate-skills/candidate-skills.styled";
import FeedbackStatus from "./components/feedback-status/feedback-status";
import JobOfferStatus from "./components/job-offer/job-offer-status";
import Language from "./components/language/language";
import {
  AboutContent,
  CommentSection,
  Container,
  Content,
  LoadingContent,
  Section,
  SectionBlock,
  Sidebar,
  Text,
  Title,
} from "./talent.styled";
import MoveLocations from "./components/move-locations/move-locations";
import Comments from "../comment-modal/components/comments";
import Tags from "../home-components/table/components/tag";
import { Tag } from "../../models/common/tag.model";
import useUmamiTracking from "../../hooks/use-umami-tracking";
import { Events } from "../../utils/constants";
import { InternalStates } from "./models";
import { usePrevious } from "../../utils/functions.utils";

interface TalentProps {
  talentId: string;
  userId?: string;
  trackId?: string;
  jobSetupId?: string;
  ecosystem?: string;
  ecosystemTech?: string;
  isNotifyOpenCandidateDetail?: boolean;
  showJobOrigin?: boolean;
  jobName?: string;
  currencyIso?: string;
  setIsJobOfferOpen?: (isOpen: boolean) => void;
  //REMOVE THIS, IT`S ONLY FOR TESTING
  publicId: string;
}

const TalentV2: React.FC<TalentProps> = ({
  talentId,
  userId,
  trackId: selectedTrack,
  jobSetupId,
  ecosystem,
  ecosystemTech,
  isNotifyOpenCandidateDetail,
  showJobOrigin,
  jobName,
  currencyIso,
  setIsJobOfferOpen,
  //REMOVE THIS, IT`S ONLY FOR TESTING
  publicId
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isRanking, isTrack } = usePages();
  const [isOpenJobOffer, setIsOpenJobOffer] = useState(false);
  const [translate, setTranslate] = useState<boolean>(false);
  const [thrigerScroll, setThrigerScroll] = useState<boolean>(false);
  const pushUmami = useUmamiTracking();

  const { currentEventTrackData } = useSelector(
    (state: AppState) => state.eventState
  );
  const { candidateDetailData, candidateDetailRankingData } = useSelector(
    (state: AppState) => state.candidateState
  );
  const { data: selectedCandidatesData } = useSelector(
    (state: AppState) => state.eventState.selectedCandidatesData
  );
  const { user } = useSelector((state: AppState) => state.authState);

  const selectedCandidateJobOffer = useMemo(() => {
    let candidateFromSelected =
      userId &&
      selectedCandidatesData?.results.find(
        (currentTalent) => currentTalent.user_id === userId
      )?.job_offer;

    return candidateFromSelected;
  }, [selectedCandidatesData, userId]);

  const trackId = useMemo(() => {
    return currentEventTrackData.data?.uuid || selectedTrack || "";
  }, [currentEventTrackData.data?.uuid, selectedTrack]);

  const isJob = useMemo((): boolean => {
    return !!currentEventTrackData.data?.is_from_job;
  }, [currentEventTrackData.data]);

  const hasLastAction = useMemo(() => {
    const last_action = candidateDetailData.data?.last_action;
    return !!last_action && last_action.status !== LastActionStatus.NOT_SUITABLE;
  }, [candidateDetailData.data]);

  const isLocked = useMemo(() => {
    return (!hasLastAction);
  }, [hasLastAction]);

  const { requestUnlockCandidate, requestCandidateNotAfordable } = useCandidateActions({
    talentId,
    trackId,
    jobSetupId,
    ecosystem,
  });

  const handleConfirm = useCallback(() => {
    requestUnlockCandidate();
    setIsOpenJobOffer(false);
  }, [requestUnlockCandidate]);

  const handleUnlockCandidate = useCallback(() => {
    pushUmami(Events.UNLOCK_CV, {
      ...candidateDetailData?.data
    }, user);
    if (isRanking || isTrack) {
      return setIsOpenJobOffer(true);
    } else {
      handleConfirm();
    }
  }, [isRanking, isTrack, handleConfirm, pushUmami, user, candidateDetailData?.data]);

  const handleNotAfordable = useCallback(() => {
    requestCandidateNotAfordable();
  }, [requestCandidateNotAfordable]);

  useEffect(() => {
    if(isRanking) {
      dispatch(clearCurrentEventTrackData());
    }
  }, [dispatch, isRanking]);

  const handleFetchCandidate = useCallback(() => {
    dispatch(fetchCandidate(talentId, trackId, ecosystem, currencyIso));
    dispatch(fetchCandidateDetailOfRanking(publicId));
  }, [currencyIso, dispatch, ecosystem, talentId, trackId, publicId]);

  const handleTagsChanged = useCallback(() => {
    handleFetchCandidate();
    setThrigerScroll(true);
  }, [handleFetchCandidate]);

  useEffect(() => {
    if(thrigerScroll && !candidateDetailData.loading) {
      const element = document.getElementById("modal-candidate-tags");
      element?.scrollIntoView({ behavior: "smooth" });
      setThrigerScroll(false);
    }
  }, [thrigerScroll, candidateDetailData.loading]);

  useEffect(() => {
    handleFetchCandidate();
    if (isNotifyOpenCandidateDetail) {
      dispatch(notifyOpenCandidateDetail(talentId, trackId));
    }
  }, [dispatch, handleFetchCandidate, isNotifyOpenCandidateDetail, talentId, trackId]);

  useEffect(() => {
    if (talentId) dispatch(fetchRecruiterComments(talentId));
  }, [dispatch, talentId]);

  const tags = useMemo((): Tag[] => {
    if(candidateDetailData.data && candidateDetailData.data.tags) {
      return candidateDetailData.data.tags;
    }
    return [];
  }, [candidateDetailData]);

  const prevAmount = usePrevious<InternalStates>({ candidateDetailData, user});
  useEffect(() => {
    if (!candidateDetailData?.data || !user || 
      prevAmount?.candidateDetailData?.data?.id === candidateDetailData?.data?.id) return;;
    pushUmami(Events.VIEW_PROFILE, {
      ...candidateDetailData?.data
    }, user);
  }, [candidateDetailData, user, pushUmami, prevAmount]);
    

  useEffect(() => {
    setIsJobOfferOpen && setIsJobOfferOpen(isOpenJobOffer);
  }, [isOpenJobOffer, setIsJobOfferOpen]);
  
  return (
    <Container id="modal-candidate">
      {candidateDetailData.loading ? (
        <LoadingContent>
          <LoadingIcon size={80} />
          {i18n.language !== "pt-BR" && (
            <Text>
              {t("talent.translateMessage", { ns: "private-home-page" })}
            </Text>
          )}
        </LoadingContent>
      ) : (
        <>
          <Header 
            candidate={candidateDetailData.data} 
            isLocked={isLocked} 
            translate={translate} 
            setTranslate={setTranslate}
          />
          <AboutContent>
            {(isRanking || isTrack) &&
              isOpenJobOffer &&
              (isJob ? (
                <ModalMatch
                  onClick={handleConfirm}
                  onClose={() => setIsOpenJobOffer(false)}
                />
              ) : (
                <JobOfferModal
                  isOpen={isOpenJobOffer}
                  onConfirm={handleConfirm}
                  onDismiss={() => setIsOpenJobOffer(false)}
                />
              ))}
            <Content>
              <About candidate={candidateDetailData.data} isLocked={isLocked} translate={translate} professionalExperiences={candidateDetailRankingData.data?.experiences}/>
              <CommentSection>
                <Comments candidateId={talentId} /> 
              </CommentSection>
            </Content>
            <Sidebar>
            {!candidateDetailData.data?.last_action ||
              candidateDetailData.data.last_action.status ===
                LastActionStatus.INTERVIEW ? (
                <Section>
                  <Actions
                    talentId={talentId}
                    trackId={trackId}
                    jobSetupId={jobSetupId}
                    isLocked={!hasLastAction}
                    onUnlockCandidate={handleUnlockCandidate}
                    onNotAfordable={handleNotAfordable}
                  />
                </Section>
              ) : (
                <Section>
                  <FeedbackStatus
                    status={candidateDetailData.data.last_action.status}
                  />
                </Section>
              )}
              {showJobOrigin && jobName && (
                <SectionBlock>
                  <Title>
                    {t("talent.applied_job", { ns: "private-home-page" })}
                  </Title>
                  <Skill style={{ height: "fit-content" }}>{jobName}</Skill>
                </SectionBlock>
              )}
              {!candidateDetailData.loading && selectedCandidateJobOffer && (
                <SectionBlock>
                  <Title>
                    {t("talent.candidate_interest", {
                      ns: "private-home-page",
                    })}
                  </Title>

                  <JobOfferStatus
                    status={selectedCandidateJobOffer.status}
                    isDetailed={true}
                  />
                  <JobOffer
                    joboffer={selectedCandidateJobOffer}
                    isLocked={isLocked}
                  />
                </SectionBlock>
              )}
              {candidateDetailData.data &&
                candidateDetailData.data?.skills?.length > 0 && (
                  <CandidateSkills candidate={candidateDetailData.data} />
                )}

              <Spotlight candidate={candidateDetailData.data} tech={ecosystemTech} 
                currencyIso={candidateDetailData.data?.wage_claim_currency} />

              {candidateDetailData.data?.move_location &&
                candidateDetailData.data?.move_location.length > 0 && (
                  <MoveLocations moveLocations={candidateDetailData.data?.move_location} />
                )}

              {candidateDetailData.data?.languages &&
                candidateDetailData.data?.languages.length > 0 && (
                  <Language languages={candidateDetailData.data?.languages} />
                )}

              <PcDs candidate={candidateDetailData.data} isLocked={isLocked} />
              {candidateDetailData.data &&
                candidateDetailData.data.time_availability &&
                candidateDetailData.data?.time_availability.length > 0 && (
                  <TimeAvailability
                    timeAvailability={
                      candidateDetailData.data.time_availability
                    }
                    isLocked={isLocked}
                  />
                )}
              {talentId && !isLocked && (
                <Tags 
                  tags={tags}
                  candidateId={talentId}
                  tagsChanged={handleTagsChanged}
                  layout="talent"
                  id="modal-candidate-tags"
                />
              )}
            </Sidebar>
          </AboutContent>
        </>
      )}
    </Container>
  );
};

export default TalentV2;